'use client'

import {
    AccountingDashboard,
    getAccountingDashboard,
    getAccountingOnboardingStatus,
    getReprocessJobStatus,
    getReprocessStatus,
    reprocessAllBackground,
    reprocessUnclassifiedBillings,
    ReprocessJobStatus,
    exportLivroDiario,
    exportSpedEcd,
} from '@/lib/api/financial/financial-accounting-api'
import { useEffect, useState } from 'react'
import AccountingOnboardingWizard from './components/accounting-onboarding-wizard'
import {
    MdArrowBack,
    MdAutoAwesome,
    MdAutorenew,
    MdBookmark,
    MdCheckCircle,
    MdFilterList,
    MdPending,
    MdReceipt,
    MdSmartToy,
    MdTrendingDown,
    MdTrendingUp,
    MdWarning,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

const formatCurrency = (cents: number) =>
    (cents / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    })

const getDefaultDates = () => {
    const now = new Date()
    const start = new Date(now.getFullYear(), now.getMonth(), 1)
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    return {
        startDate: start.toISOString().slice(0, 10),
        endDate: end.toISOString().slice(0, 10),
    }
}

export default function AccountingDashboardPage() {
    const navigate = useNavigate()
    const [data, setData] = useState<AccountingDashboard | null>(null)
    const [loading, setLoading] = useState(true)
    const [startDate, setStartDate] = useState(getDefaultDates().startDate)
    const [endDate, setEndDate] = useState(getDefaultDates().endDate)
    const [showWizard, setShowWizard] = useState(false)
    const [unclassifiedCount, setUnclassifiedCount] = useState<number>(0)
    const [reprocessing, setReprocessing] = useState(false)
    const [jobStatus, setJobStatus] = useState<ReprocessJobStatus | null>(null)

    useEffect(() => {
        loadData()
    }, [startDate, endDate])

    // Wizard de onboarding: ao primeiro acesso (sem plano/regras/caixa),
    // abre automaticamente o setup contabil.
    useEffect(() => {
        getAccountingOnboardingStatus()
            .then(res => {
                if (res.success && res.result && !res.result.completed) {
                    setShowWizard(true)
                }
            })
            .catch(() => {})
        loadReprocessStatus()
    }, [])

    const loadReprocessStatus = async () => {
        const res = await getReprocessStatus()
        if (res.success && res.result) setUnclassifiedCount(res.result.count)
        const jobRes = await getReprocessJobStatus()
        if (jobRes.success && jobRes.result) setJobStatus(jobRes.result)
    }

    const handleReprocess = async () => {
        setReprocessing(true)
        try {
            const res = await reprocessUnclassifiedBillings(500)
            if (res.success && res.result) {
                toast.success(
                    `${res.result.processed} processados (${res.result.autoClassified} classificados, ${res.result.pending} pendentes de revisão)`
                )
                await Promise.all([loadData(), loadReprocessStatus()])
            } else {
                toast.error(res.error || 'Erro ao reprocessar')
            }
        } finally {
            setReprocessing(false)
        }
    }

    const handleReprocessAllBackground = async () => {
        const res = await reprocessAllBackground()
        if (res.success && res.result) {
            if (res.result.alreadyRunning) {
                toast.info('Processamento em background já está em execução')
            } else {
                toast.success('Processamento em background iniciado')
            }
            await loadReprocessStatus()
        } else {
            toast.error(res.error || 'Erro ao iniciar background')
        }
    }

    // Polling do job em background: a cada 5s enquanto active/waiting
    useEffect(() => {
        if (!jobStatus?.exists) return
        if (
            jobStatus.state !== 'active' &&
            jobStatus.state !== 'waiting' &&
            jobStatus.state !== 'delayed'
        )
            return
        const interval = setInterval(() => {
            getReprocessJobStatus().then(res => {
                if (res.success && res.result) {
                    setJobStatus(res.result)
                    if (
                        res.result.state === 'completed' ||
                        res.result.state === 'failed'
                    ) {
                        loadData()
                        loadReprocessStatus()
                    }
                }
            })
            getReprocessStatus().then(res => {
                if (res.success && res.result)
                    setUnclassifiedCount(res.result.count)
            })
        }, 5000)
        return () => clearInterval(interval)
    }, [jobStatus?.state, jobStatus?.exists])

    const loadData = async () => {
        setLoading(true)
        try {
            const response = await getAccountingDashboard(startDate, endDate)
            if (response.success && response.result) setData(response.result)
        } catch {
            toast.error('Erro ao carregar dashboard')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="p-4 md:p-6 max-w-7xl mx-auto">
            {showWizard && (
                <AccountingOnboardingWizard
                    onClose={() => setShowWizard(false)}
                    onCompleted={() => {
                        setShowWizard(false)
                        loadData()
                        loadReprocessStatus()
                    }}
                />
            )}

            {jobStatus?.exists &&
                (jobStatus.state === 'active' ||
                    jobStatus.state === 'waiting' ||
                    jobStatus.state === 'delayed') && (
                    <div className="alert alert-info mb-4 flex flex-row items-center gap-3">
                        <span className="loading loading-spinner loading-sm" />
                        <div className="flex-1 text-sm">
                            <strong>
                                Processamento em background em andamento.
                            </strong>{' '}
                            Restam{' '}
                            <strong>
                                {unclassifiedCount.toLocaleString('pt-BR')}
                            </strong>{' '}
                            billings. Esta tela atualiza a cada 5s.
                            {jobStatus.progress?.message && (
                                <span className="block text-xs text-base-content/60 mt-1">
                                    {String(jobStatus.progress.message)}
                                </span>
                            )}
                        </div>
                    </div>
                )}

            {jobStatus?.state === 'completed' && jobStatus.returnvalue && (
                <div className="alert alert-success mb-4 flex flex-row items-center gap-3">
                    <MdAutorenew className="text-xl" />
                    <div className="flex-1 text-sm">
                        <strong>Processamento em background concluído:</strong>{' '}
                        {jobStatus.returnvalue.totalProcessed.toLocaleString(
                            'pt-BR'
                        )}{' '}
                        billings,{' '}
                        {jobStatus.returnvalue.autoClassified.toLocaleString(
                            'pt-BR'
                        )}{' '}
                        classificados automaticamente,{' '}
                        {jobStatus.returnvalue.pending.toLocaleString('pt-BR')}{' '}
                        pendentes de revisão.
                    </div>
                </div>
            )}

            {unclassifiedCount > 0 &&
                (!jobStatus?.exists ||
                    (jobStatus.state !== 'active' &&
                        jobStatus.state !== 'waiting' &&
                        jobStatus.state !== 'delayed')) && (
                    <div className="alert alert-warning mb-4 flex flex-row flex-wrap items-center gap-3">
                        <MdAutorenew className="text-xl" />
                        <div className="flex-1 text-sm min-w-[200px]">
                            <strong>
                                {unclassifiedCount.toLocaleString('pt-BR')}
                            </strong>{' '}
                            lançamentos contábeis pendentes. O DRE e Balancete
                            só mostram dados após classificação.
                        </div>
                        {/*<div className="flex gap-2">
                            <button
                                className="btn btn-sm btn-outline"
                                onClick={handleReprocess}
                                disabled={reprocessing}
                            >
                                {reprocessing ? (
                                    <span className="loading loading-spinner loading-xs" />
                                ) : (
                                    'Processar 500'
                                )}
                            </button>
                            <button
                                className="btn btn-sm btn-warning"
                                onClick={handleReprocessAllBackground}
                                disabled={reprocessing}
                            >
                                Processar tudo em background
                            </button>
                        </div>*/}
                    </div>
                )}

            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => navigate(-1)}
                    >
                        <MdArrowBack size={20} />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold">Contabilidade</h1>
                        <p className="text-base-content/60 text-sm mt-1">
                            Recursos contábeis
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <input
                        type="date"
                        className="input input-bordered input-sm"
                        value={startDate}
                        onChange={e => setStartDate(e.target.value)}
                    />
                    <input
                        type="date"
                        className="input input-bordered input-sm"
                        value={endDate}
                        onChange={e => setEndDate(e.target.value)}
                    />
                </div>
            </div>

            {loading && (
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="loading loading-spinner loading-lg text-secondary"></div>
                </div>
            )}

            {!loading && data && (
                <div className="flex flex-col gap-6">
                    {/* Alertas */}
                    {(data.alerts.pendingEntries > 0 ||
                        data.alerts.unclassifiedBillings > 0) && (
                        <div className="alert alert-warning">
                            <MdWarning size={20} />
                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                                {data.alerts.pendingEntries > 0 && (
                                    <span className="text-sm">
                                        {data.alerts.pendingEntries}{' '}
                                        lançamento(s) pendente(s) de
                                        classificação
                                    </span>
                                )}
                                {data.alerts.unclassifiedBillings > 0 && (
                                    <span className="text-sm">
                                        {data.alerts.unclassifiedBillings}{' '}
                                        cobrança(s) sem lançamento contábil
                                    </span>
                                )}
                            </div>
                            <button
                                className="btn btn-sm btn-warning"
                                onClick={() =>
                                    navigate(
                                        '/financial/lancamentos-contabeis?status=PENDING'
                                    )
                                }
                            >
                                Ver pendentes
                            </button>
                        </div>
                    )}

                    {/* Cards financeiros */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="card bg-base-100 shadow-sm border border-base-300/50">
                            <div className="card-body p-4">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-base-content/60">
                                        Receitas
                                    </span>
                                    <MdTrendingUp className="text-success text-xl" />
                                </div>
                                <p className="text-2xl font-bold text-success">
                                    {formatCurrency(
                                        data.financials.totalIncome
                                    )}
                                </p>
                            </div>
                        </div>
                        <div className="card bg-base-100 shadow-sm border border-base-300/50">
                            <div className="card-body p-4">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-base-content/60">
                                        Despesas
                                    </span>
                                    <MdTrendingDown className="text-error text-xl" />
                                </div>
                                <p className="text-2xl font-bold text-error">
                                    {formatCurrency(
                                        data.financials.totalExpense
                                    )}
                                </p>
                            </div>
                        </div>
                        <div className="card bg-base-100 shadow-sm border border-base-300/50">
                            <div className="card-body p-4">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-base-content/60">
                                        Resultado Líquido
                                    </span>
                                    <MdReceipt className="text-secondary text-xl" />
                                </div>
                                <p
                                    className={`text-2xl font-bold ${data.financials.netResult >= 0 ? 'text-success' : 'text-error'}`}
                                >
                                    {formatCurrency(data.financials.netResult)}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Cards de status e origem */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="card bg-base-100 shadow-sm border border-base-300/50">
                            <div className="card-body p-4 items-center text-center">
                                <MdBookmark className="text-2xl text-secondary" />
                                <p className="text-2xl font-bold">
                                    {data.totals.entries}
                                </p>
                                <p className="text-xs text-base-content/60">
                                    Total
                                </p>
                            </div>
                        </div>
                        <div className="card bg-base-100 shadow-sm border border-base-300/50">
                            <div className="card-body p-4 items-center text-center">
                                <MdPending className="text-2xl text-warning" />
                                <p className="text-2xl font-bold">
                                    {data.totals.pending}
                                </p>
                                <p className="text-xs text-base-content/60">
                                    Pendentes
                                </p>
                            </div>
                        </div>
                        <div className="card bg-base-100 shadow-sm border border-base-300/50">
                            <div className="card-body p-4 items-center text-center">
                                <MdCheckCircle className="text-2xl text-success" />
                                <p className="text-2xl font-bold">
                                    {data.totals.reviewed}
                                </p>
                                <p className="text-xs text-base-content/60">
                                    Revisados
                                </p>
                            </div>
                        </div>
                        <div className="card bg-base-100 shadow-sm border border-base-300/50">
                            <div className="card-body p-4 items-center text-center">
                                <MdSmartToy className="text-2xl text-secondary" />
                                <p className="text-2xl font-bold">
                                    {data.origins.ai + data.origins.automatic}
                                </p>
                                <p className="text-xs text-base-content/60">
                                    Automáticos + IA
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Links rápidos */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                            {
                                label: 'Lançamentos',
                                href: '/financial/journal-entries',
                                icon: MdBookmark,
                            },
                            {
                                label: 'Regras',
                                href: '/financial/accounting-rules',
                                icon: MdFilterList,
                            },
                            {
                                label: 'DRE',
                                href: '/financial/relatorios/dre',
                                icon: MdReceipt,
                            },
                            {
                                label: 'Balancete',
                                href: '/financial/relatorios/balancete',
                                icon: MdReceipt,
                            },
                        ].map(link => (
                            <button
                                key={link.href}
                                className="btn btn-outline btn-sm gap-2"
                                onClick={() => navigate(link.href)}
                            >
                                <link.icon size={16} />
                                {link.label}
                            </button>
                        ))}
                    </div>

                    {/* Lançamentos pendentes recentes */}
                    {data.recentPending.length > 0 && (
                        <div className="card bg-base-100 shadow-sm border border-base-300/50">
                            <div className="card-body p-4">
                                <h3 className="font-semibold text-sm mb-3">
                                    Pendentes de classificação
                                </h3>
                                <div className="space-y-2">
                                    {data.recentPending.map((entry: any) => (
                                        <div
                                            key={entry.id}
                                            className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-base-200/50 cursor-pointer"
                                            onClick={() =>
                                                navigate(
                                                    '/financial/lancamentos-contabeis?status=PENDING'
                                                )
                                            }
                                        >
                                            <div>
                                                <p className="text-sm">
                                                    {entry.description}
                                                </p>
                                                {entry.billing && (
                                                    <p className="text-xs text-base-content/50">
                                                        Cobrança #
                                                        {entry.billingId} -{' '}
                                                        {formatCurrency(
                                                            entry.billing.amount
                                                        )}
                                                    </p>
                                                )}
                                            </div>
                                            <span className="badge badge-warning badge-xs">
                                                Pendente
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Exportação */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                        {/* Livro Diário */}
                        <div className="card bg-base-100 shadow-sm border border-base-300/50">
                            <div className="card-body p-5">
                                <h3 className="font-bold text-base-content mb-3">
                                    Livro Diário
                                </h3>
                                <p className="text-sm text-base-content/60 mb-3">
                                    Exportar todos os lançamentos contábeis em
                                    ordem cronológica
                                </p>
                                <div className="flex gap-2">
                                    <button
                                        className="btn btn-sm btn-outline"
                                        onClick={() => {
                                            exportLivroDiario(
                                                startDate,
                                                endDate,
                                                'xlsx'
                                            ).catch(() =>
                                                toast.error('Erro ao exportar')
                                            )
                                        }}
                                    >
                                        Excel
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline"
                                        onClick={() => {
                                            exportLivroDiario(
                                                startDate,
                                                endDate,
                                                'pdf'
                                            ).catch(() =>
                                                toast.error('Erro ao exportar')
                                            )
                                        }}
                                    >
                                        PDF
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* SPED ECD */}
                        <div className="card bg-base-100 shadow-sm border border-base-300/50">
                            <div className="card-body p-5">
                                <h3 className="font-bold text-base-content mb-3">
                                    SPED ECD
                                </h3>
                                <p className="text-sm text-base-content/60 mb-3">
                                    Gerar arquivo da Escrituração Contábil
                                    Digital para a Receita Federal
                                </p>
                                <div className="alert alert-warning text-xs py-2 mb-3">
                                    <span>
                                        <strong>Versão preliminar.</strong> O arquivo serve
                                        para conferência e para entregar ao contador — ainda
                                        não passa na validação do PVA da Receita, então não
                                        use para transmitir.
                                    </span>
                                </div>
                                <div className="flex items-end gap-2">
                                    <div className="form-control">
                                        <label className="label py-0">
                                            <span className="label-text text-xs">
                                                Ano fiscal
                                            </span>
                                        </label>
                                        <select
                                            className="select select-bordered select-sm w-28"
                                            id="sped-year"
                                            defaultValue={new Date().getFullYear()}
                                        >
                                            {Array.from(
                                                { length: 5 },
                                                (_, i) =>
                                                    new Date().getFullYear() - i
                                            ).map(y => (
                                                <option key={y} value={y}>
                                                    {y}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <button
                                        className="btn btn-sm btn-secondary"
                                        onClick={() => {
                                            const yearEl =
                                                document.getElementById(
                                                    'sped-year'
                                                ) as HTMLSelectElement
                                            const year = parseInt(
                                                yearEl?.value ||
                                                    String(
                                                        new Date().getFullYear()
                                                    ),
                                                10
                                            )
                                            exportSpedEcd(year).catch(() =>
                                                toast.error(
                                                    'Erro ao gerar SPED ECD'
                                                )
                                            )
                                        }}
                                    >
                                        Gerar SPED ECD
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
